import { useToast } from '../contexts/ToastContext';

/**
 * Custom hook for common API-related toast patterns
 */
export const useApiToast = () => {
  const { showSuccess, showError, showLoading, removeToast } = useToast();

  /**
   * Handle API request with loading and result toasts
   */
  const handleApiRequest = async (
    apiCall,
    {
      loadingMessage = 'Processing...',
      successMessage = 'Operation completed successfully',
      errorMessage = 'An error occurred',
      showSuccessToast = true,
      showErrorToast = true
    } = {}
  ) => {
    let loadingToastId;

    try {
      // Show loading toast
      loadingToastId = showLoading(loadingMessage);

      // Execute API call
      const result = await apiCall();

      // Remove loading toast
      if (loadingToastId) removeToast(loadingToastId);

      // Show success toast
      if (showSuccessToast) {
        showSuccess(successMessage);
      }

      return result;
    } catch (error) {
      // Remove loading toast
      if (loadingToastId) removeToast(loadingToastId);

      // Show error toast
      if (showErrorToast) {
        showError(errorMessage, {
          message: error.message
        });
      }

      throw error; // Re-throw for caller to handle if needed
    }
  };

  /**
   * Handle wallet operations
   */
  const handleWalletOperation = async (
    operation,
    {
      operationType = 'operation',
      loadingMessage,
      successMessage,
      errorMessage
    } = {}
  ) => {
    const messages = {
      create: {
        loading: 'Creating wallet...',
        success: 'Wallet created successfully!',
        error: 'Failed to create wallet'
      },
      connect: {
        loading: 'Connecting wallet...',
        success: 'Wallet connected successfully!',
        error: 'Failed to connect wallet'
      },
      disconnect: {
        loading: 'Disconnecting wallet...',
        success: 'Wallet disconnected',
        error: 'Failed to disconnect wallet'
      },
      send: {
        loading: 'Sending transaction...',
        success: 'Transaction sent successfully!',
        error: 'Failed to send transaction'
      },
      delete: {
        loading: 'Deleting wallet...',
        success: 'Wallet deleted successfully',
        error: 'Failed to delete wallet'
      }
    };

    const config = messages[operationType] || messages.operation;

    return handleApiRequest(operation, {
      loadingMessage: loadingMessage || config.loading,
      successMessage: successMessage || config.success,
      errorMessage: errorMessage || config.error
    });
  };

  /**
   * Handle token operations
   */
  const handleTokenOperation = async (
    operation,
    {
      operationType = 'operation',
      tokenSymbol = 'token',
      loadingMessage,
      successMessage,
      errorMessage
    } = {}
  ) => {
    const messages = {
      add: {
        loading: `Adding ${tokenSymbol} to watchlist...`,
        success: `${tokenSymbol} added to watchlist!`,
        error: `Failed to add ${tokenSymbol} to watchlist`
      },
      remove: {
        loading: `Removing ${tokenSymbol} from watchlist...`,
        success: `${tokenSymbol} removed from watchlist`,
        error: `Failed to remove ${tokenSymbol} from watchlist`
      },
      buy: {
        loading: `Buying ${tokenSymbol}...`,
        success: `Successfully bought ${tokenSymbol}!`,
        error: `Failed to buy ${tokenSymbol}`
      },
      sell: {
        loading: `Selling ${tokenSymbol}...`,
        success: `Successfully sold ${tokenSymbol}!`,
        error: `Failed to sell ${tokenSymbol}`
      }
    };

    const config = messages[operationType] || messages.operation;

    return handleApiRequest(operation, {
      loadingMessage: loadingMessage || config.loading,
      successMessage: successMessage || config.success,
      errorMessage: errorMessage || config.error
    });
  };

  /**
   * Handle trading operations
   */
  const handleTradingOperation = async (
    operation,
    {
      operationType = 'trade',
      tokenSymbol = 'token',
      amount = '',
      loadingMessage,
      successMessage,
      errorMessage
    } = {}
  ) => {
    const amountText = amount ? ` ${amount}` : '';

    const messages = {
      trade: {
        loading: `Processing${amountText} ${tokenSymbol} trade...`,
        success: `Trade completed successfully!`,
        error: `Trade failed`
      },
      quote: {
        loading: `Getting quote for ${tokenSymbol}...`,
        success: `Quote retrieved successfully`,
        error: `Failed to get quote`
      },
      approve: {
        loading: `Approving ${tokenSymbol}...`,
        success: `${tokenSymbol} approved successfully`,
        error: `Failed to approve ${tokenSymbol}`
      }
    };

    const config = messages[operationType] || messages.trade;

    return handleApiRequest(operation, {
      loadingMessage: loadingMessage || config.loading,
      successMessage: successMessage || config.success,
      errorMessage: errorMessage || config.error
    });
  };

  return {
    handleApiRequest,
    handleWalletOperation,
    handleTokenOperation,
    handleTradingOperation
  };
};